/**
 * 精确浮点运算的三方库
 */

import BigNumber from 'bignumber.js';

const defaultFormat = {
  decimalSeparator: '.',
  groupSeparator: ',',
  groupSize: 3,
  secondaryGroupSize: 0,
  fractionGroupSeparator: ' ',
  fractionGroupSize: 0,
};

/**
 * 科学计数法 => 字符串
 */
const toNonExponential = (num: number | string) => {
  const value = new BigNumber(num).valueOf();
  if (value.includes('e')) {
    const m = Number(num)
      .toExponential()
      .match(/\d(?:\.(\d*))?e([+-]\d+)/);
    return m?.length ? Number(num).toFixed(Math.max(0, (m[1] || '').length - +m[2])) : value;
  }
  return value;
};

BigNumber.config({
  // 向0方向舍入
  ROUNDING_MODE: BigNumber.ROUND_DOWN,
  FORMAT: defaultFormat,
  DECIMAL_PLACES: 20,
});

/**
 * @加法
 * @params 数字 accPlus(0.1, 0.2) => 0.3
 */
const accPlus = (a: BigNumber.Value, b: BigNumber.Value) => new BigNumber(a).plus(new BigNumber(b)).valueOf();

/**
 * @减法
 * @params 数字 accMinus(0.1, 0.11) =>  -0.01
 */
const accMinus = (a: BigNumber.Value, b: BigNumber.Value) => new BigNumber(a).minus(new BigNumber(b)).valueOf();

/**
 * @乘法
 * @params 数字 accTimes(1,2)
 */
const accTimes = (a: BigNumber.Value, b: BigNumber.Value) => new BigNumber(a).multipliedBy(new BigNumber(b)).valueOf();

/**
 * @除法
 * @param {nunbert} a 数字 accDivide(13, 1.6)
 * @param {nunbert} b 数字 accDivide(13, 1.6)
 */
const accDivide = (a: BigNumber.Value, b: BigNumber.Value) => new BigNumber(a).div(new BigNumber(b)).valueOf();

// 递归运算，支持3个以上的多参数运算
const countReduce = (func: Function, ...args: any[]) => {
  const result = args.reduce((count, item, index) => {
    if (index === 0) return count;
    count = func(count, item);
    return count;
  }, args[0]);

  return Number(result);
};

/**
 * @加法
 * @params 数字 accPlus(0.1, 0.2) => 0.3
 */
export const bignumberPlus = (...args: any[]): number => countReduce(accPlus, ...args);

/**
 * @减法
 * @params 数字 accMinus(0.1, 0.11) =>  -0.01
 */
export const bignumberMinus = (...args: any[]): number => countReduce(accMinus, ...args);

/**
 * @乘法
 * @params 数字 accTimes(1,2)
 */
export const bignumberTimes = (...args: any[]): number => countReduce(accTimes, ...args);

/**
 * @除法
 * @param {nunbert} a 数字 accDivide(13, 1.6)
 * @param {nunbert} b 数字 accDivide(13, 1.6)
 */
export const bignumberDivide = (...args: any[]): number => countReduce(accDivide, ...args);

/**
 * 数字格式化
 * 千分位且默认保留2位小数
 * 运算不做4舍5入操作
 * @params 数字 toFormat(1001.33999, 2) =>  1,001.33
 */
export const bignumberToFormat = (num: number | string, precision = 2): string =>
  new BigNumber(num).toFormat(precision);

/**
 * 转数字类型
 * 该方法有可能会转为科学技术法，慎用
 */
export const bignumberToNumber = (num: number | string) => new BigNumber(num).toNumber();

export const bignumberToFixed = (num: number | string, precision?: number) => {
  if (precision !== undefined) {
    return toNonExponential(new BigNumber(num).toFixed(precision));
  }
  return toNonExponential(new BigNumber(num).toFixed());
};
